import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { MailCheck, Eye, Search, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PER_PAGE = 50;

const fmtDataOra = (iso) => (iso ? new Date(iso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '-');

export default function LogEmailPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [data, setData] = useState('');
  const [loading, setLoading] = useState(true);
  const [dettaglio, setDettaglio] = useState(null);
  const [loadingDettaglio, setLoadingDettaglio] = useState(false);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * PER_PAGE, limit: PER_PAGE };
      if (search.trim()) params.search = search.trim();
      if (data) params.data = data;
      const res = await axios.get(`${API}/email-log`, { params });
      setItems(res.data.items);
      setTotal(res.data.total);
    } catch (e) {
      toast.error('Errore caricamento log email');
    } finally {
      setLoading(false);
    }
  }, [page, search, data]);

  useEffect(() => {
    const t = setTimeout(fetchLog, 300);
    return () => clearTimeout(t);
  }, [fetchLog]);

  useEffect(() => { setPage(0); }, [search, data]);

  const apriContenuto = async (log) => {
    setLoadingDettaglio(true);
    try {
      const res = await axios.get(`${API}/email-log/${log.id}/contenuto`);
      setDettaglio(res.data);
    } catch (e) {
      toast.error('Errore caricamento contenuto');
    } finally {
      setLoadingDettaglio(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="log-email-page">
      <div>
        <h1 className="text-3xl font-black tracking-tight flex items-center gap-3">
          <MailCheck className="h-8 w-8 text-cyan-500" />
          Log Email
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Tutte le email inviate automaticamente dal sistema, dall'inizio ({total} totali)
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1 sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per indirizzo email o oggetto..."
            className="rounded-none pl-9"
            data-testid="log-email-search"
          />
        </div>
        <div className="flex items-center gap-1">
          <Input
            type="date"
            value={data}
            onChange={(e) => setData(e.target.value)}
            className="rounded-none sm:w-44"
            data-testid="log-email-data"
          />
          {data && (
            <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => setData('')} title="Rimuovi filtro data" data-testid="log-email-data-reset">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <Card className="rounded-md">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Data e ora</th>
                  <th>Destinatario</th>
                  <th className="hidden md:table-cell">Categoria</th>
                  <th>Oggetto</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="text-center py-10"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={5} className="text-center text-muted-foreground py-10">Nessuna email trovata</td></tr>
                ) : (
                  items.map((log) => (
                    <tr key={log.id} className="hover:bg-muted transition-colors">
                      <td className="font-mono text-xs whitespace-nowrap">{fmtDataOra(log.sent_at)}</td>
                      <td className="text-sm max-w-[240px] truncate" title={log.to}>{log.to}</td>
                      <td className="hidden md:table-cell">
                        <Badge variant="outline" className="text-[10px] uppercase">{log.tipo}</Badge>
                      </td>
                      <td className="text-sm max-w-[320px] truncate" title={log.subject}>{log.subject}</td>
                      <td>
                        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md text-primary"
                          onClick={() => apriContenuto(log)} disabled={loadingDettaglio}
                          title="Apri e leggi il contenuto completo" data-testid={`log-email-apri-${log.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground">Pagina {page + 1} di {totalPages} — {total} email</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-none h-8" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="log-email-prec">← Prec</Button>
            <Button variant="outline" size="sm" className="rounded-none h-8" disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)} data-testid="log-email-succ">Succ →</Button>
          </div>
        </div>
      )}

      <Dialog open={!!dettaglio} onOpenChange={(open) => { if (!open) setDettaglio(null); }}>
        <DialogContent className="sm:max-w-3xl rounded-none max-h-[92vh] flex flex-col" data-testid="log-email-dialog">
          <DialogHeader className="shrink-0">
            <DialogTitle className="font-mono uppercase tracking-wider text-base">{dettaglio?.subject || 'Email'}</DialogTitle>
          </DialogHeader>
          {dettaglio && (
            <>
              <p className="text-xs text-muted-foreground shrink-0">
                Inviata il {fmtDataOra(dettaglio.sent_at)} a <strong className="text-foreground">{dettaglio.to}</strong> · categoria {dettaglio.tipo}
              </p>
              <div className="flex-1 min-h-0 border border-border bg-white">
                {dettaglio.html ? (
                  <iframe title="Contenuto email" srcDoc={dettaglio.html} sandbox="" className="w-full h-[65vh]" data-testid="log-email-iframe" />
                ) : (
                  <p className="p-6 text-sm text-muted-foreground">
                    Contenuto completo non disponibile: questa email è stata inviata prima dell'attivazione dell'archivio contenuti.
                    Data, destinatario e oggetto sono riportati sopra.
                  </p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
